import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Button, Form, FormField, Grid, Header, Icon, Input, Label, Loader, Message, Progress, Segment } from "semantic-ui-react";
import { cancelAppointment, getWeeklySchedule, setAppointment } from "../queries";
import { useTranslation } from "react-i18next";
import './UserAppointmentsMobile.css';
import { getDateFromWeekday, getTimestamp } from "../TimeStamp";
import { readFromToken } from "../auth.ts";
import errorHandler from "../ErrorHandler";
import { useAppContext } from "../AppContext";
import TextAndIcon from "../TextAndIcon.jsx";

const UserAppointmentsMobile = () => {
    const { t, i18n } = useTranslation();
    const user_course = readFromToken("course");
    const { rtl } = useAppContext();

    const isWeekdayToday = (day) => {
        const d = getDateFromWeekday(day);
        console.log(day, d.getDate(), new Date().getDay())
        return d.getDay() === new Date().getDay();
    }

    const getWeekdayText = (day) => {
        const d = getDateFromWeekday(day);
        const dayStr = (d.getDay() === new Date().getDay()) ? ("("+t('today')+")") : "";
        
        return `${d.toLocaleDateString(i18n.language, { weekday: 'long', month: 'long', day: 'numeric' })} ${dayStr}`;
    }

    const {
        data,
        error, 
        isLoading,
        refetch: refetchSchedule,
    } = useQuery("getWeeklySchedule", getWeeklySchedule);

    const setAppointmentMutation = useMutation(setAppointment, {
        onSettled: (response) => { refetchSchedule(); errorHandler(response, t) }
    });

    const cancelAppointmentMutation = useMutation(cancelAppointment, {
        onSettled: (response) => { refetchSchedule(); errorHandler(response, t) }
    });


    const [step, setStep] = useState(0);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);

    const [nameInput, setNameInput] = useState('');
    const [courseInput, setCourseInput] = useState('');
    const [notesInput, setNotes] = useState('');
    const [nameHasError, setNameHasError] = useState(false);
    const [courseHasError, setCourseHasError] = useState(false);
    const [isScheduled, setIsScheduled] = useState(false);

    const resetInputs = () => {
        setNameInput('');
        setCourseInput(user_course);
        setNotes('');
        setIsScheduled(false);
    }

    const onBackClick = () => {
        resetInputs();
        setStep(step - 1);
    }

    const onDaySelect = (day) => {
        resetInputs();
        setSelectedDay(day);
        setStep(step + 1);
    }
    
    const onTimeSelect = (start_time, course_name, name, notes) => {
        if (course_name && name) {
            setCourseInput(course_name);
            setNameInput(name);
            setNotes(notes);
            setIsScheduled(true);
        }

        setSelectedTime(start_time);
        setStep(step + 1);
    }

    const onSubmit = () => {
        if (!nameInput || nameInput.length < 3) setNameHasError(true);
        if (!courseInput || courseInput.length < 2) setCourseHasError(true);

        if (nameInput?.length >= 3 && courseInput?.length >= 2) {
            setAppointmentMutation.mutate({ time: getTimestamp(selectedTime, selectedDay), name: nameInput, course: courseInput, notes: notesInput });
            setStep(step - 1);
            resetInputs();
        }
    }

    const onCancel = () => {
        cancelAppointmentMutation.mutate({ time: getTimestamp(selectedTime, selectedDay) });
        //resetInputs();
        setIsScheduled(false);
    }

    return (<>
    <Header size="large" textAlign="center">{t("appointment-schedule-page")}</Header>
    {isLoading ? (
        <Loader />
    ) : !data ? (
        <Message>{t("error")}</Message>
    ) : (
        <Segment className="mobile-appointments-segment">
            <Progress className="progress-bar" color="blue" size="tiny" percent={Math.ceil(33.3 * (step + 1))} />
            { step !== 0 && (
                <>
                    <Button onClick={onBackClick} size="large" className={`back-button ${rtl ? 'rtl' : ''}`} floated={rtl ? 'right' : 'left'} basic><Icon name="angle left" />{t('back')}</Button>
                    <br />
                </>
            )}
            { step === 0 ? (
                <>
                    <Header textAlign="center">{t("select-day")}</Header>
                    <div className="chips-container">
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday']
                            .filter((day) => day in data && data[day].length)
                            .map((day, index) => (
                                <Button
                                    key={day}
                                    className="chip"
                                    basic
                                    size="large"
                                    onClick={() => onDaySelect(day)}
                                    // active={selectedDay === day}
                                >
                                    <TextAndIcon icon="calendar outline" text={`${t(day)} ${isWeekdayToday(day) ? `(${t('today')})` : ''}`}></TextAndIcon>
                                </Button>
                        ))}
                    </div>
                </>
            ) : step === 1 ? (
                <>
                    <Header textAlign="center">{getWeekdayText(selectedDay)}<br/>{t("select-time")}</Header>
                    <div className="chips-container">
                        {
                            data[selectedDay].map(({ start_time, course_name, name, notes }) => (
                                <Button
                                    className="chip"
                                    basic
                                    size="large"
                                    onClick={() => onTimeSelect(start_time, course_name, name, notes)}
                                    positive={name && course_name}
                                >
                                    <TextAndIcon icon="clock outline" text={start_time}/>
                            </Button>
                            ))
                        }
                    </div>
                </>
            ) : step === 2 ? (
                <>
                    <Header textAlign="center">{
                        isScheduled ? t('appointment-is-reserved-for') : t('summary')
                    }</Header>
                    <Header.Subheader textAlign="center">{`${getWeekdayText(selectedDay)}, ${selectedTime}`}</Header.Subheader>
                    <Form className="summary-form">
                        <FormField error={nameHasError} disabled={isScheduled}>
                            <Input size="large" error={nameHasError} value={nameInput} placeholder={t('name')} onChange={(_, e) => { setNameInput(e.value); setNameHasError(false); }}/>
                            {nameHasError && (
                                <Label pointing prompt size="large">
                                    {t('name-must-have-at-least-3')}
                                </Label>
                            )}
                        </FormField>
                        <FormField error={courseHasError} disabled={isScheduled}>
                            <Input size="large" error={courseHasError} placeholder={t('course')} value={courseInput} onChange={(_, e) => { setCourseInput(e.value); setCourseHasError(false); }} />
                            {courseHasError && (
                                <Label pointing prompt size="large">
                                    {t('course-must-have-at-least-2')}
                                </Label>
                            )}
                        </FormField>
                        <FormField disabled={isScheduled}>
                            <Input size="large" placeholder={t('notes')} value={notesInput} onChange={(_, e) => { setNotes(e.value); }} />
                        </FormField>
                        {
                            isScheduled ? (
                                <Button color="red" size="large" onClick={onCancel}>{t('cancel-appointment')}</Button>
                            ) : (
                                <Button color="blue" size="large" onClick={onSubmit}>{t('set-appointment')}</Button>
                            )
                        }
                    </Form>
                </>
            ) : (
                <></>
            )}
        </Segment>
    )}</>)
}

export default UserAppointmentsMobile